import React from 'react';
import { Icon, Tooltip } from 'antd';

export const CHARGE_KEY_ALIASES = {
  cleaning: 'cleaningFee',
  bookingEngineServiceFee: 'bookingEngineService',
};

export const PAYOUT_CALC_METHODS = {
  perDayAndTransaction: 'Per Day and Transaction',
  perTransaction: 'Per Transaction',
  perCheckOut: 'Per Check-out',
  perAverage: 'By Average',
};

export const EXPENSE_SETTING_ROWS = [
  { key: 'water', label: 'Water' },
  { key: 'electricity', label: 'Electricity' },
  { key: 'internet', label: 'Internet' },
  { key: 'cleaningFee', label: 'Cleaning Fee' },
  { key: 'laundryFee', label: 'Laundry Fee' },
  { key: 'serviceFee', label: 'Service Fee' },
  { key: 'checkInFee', label: 'Check In Fee' },
  { key: 'checkOutFee', label: 'Check Out Fee' },
  { key: 'toiletryFee', label: 'Toiletry Set Fee' },
  { key: 'otherFee', label: 'Other Fee' },
  { key: 'adminFee', label: 'Administrative Charges' },
];

export const getDefaultExpensesSetting = () => ({
  water: 'shared',
  electricity: 'shared',
  internet: 'shared',
  cleaningFee: 'shared',
  laundryFee: 'shared',
  serviceFee: 'shared',
  checkInFee: 'shared',
  checkOutFee: 'shared',
  toiletryFee: 'shared',
  otherFee: 'shared',
  adminFee: 'shared',
});

export const getDefaultChargesSetting = () => ({
  rental: 'shared',
  earlyCheckIn: 'host',
  lateCheckOut: 'host',
  shuttle: 'host',
  transportation: 'host',
  breakfast: 'host',
  lunch: 'host',
  dinner: 'host',
  other: 'host',
  cleaningFee: 'host',
  extraGuest: 'shared',
  bookingEngineService: 'host',
});

export const resolvePayoutCalcMethod = value => {
  if (PAYOUT_CALC_METHODS[value]) return value;
  return 'perCheckOut';
};

export const resolveStartDay = value => {
  const n = Number(value);
  if (Number.isFinite(n) && n >= 1 && n <= 28) return Math.floor(n);
  return 1;
};

export const resolveGroupingType = value => (value === 'individual' ? 'individual' : 'pool');

export const groupingTypeLabel = value => (resolveGroupingType(value) === 'individual' ? 'Individual' : 'Pool');

const pad2 = n => (n < 10 ? `0${n}` : String(n));

const isoLocalDate = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const payoutWindow = (year, month, startDay) => {
  const y = Number(year);
  const monthIndex = Number(month) - 1;
  const day = resolveStartDay(startDay);
  const start = new Date(y, monthIndex, day);
  const end = new Date(y, monthIndex + 1, day - 1);
  const daysInWindow = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  return {
    startDate: isoLocalDate(start),
    endDate: isoLocalDate(end),
    daysInWindow,
    startDay: day,
  };
};

const toYmd = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

const utcDay = ymd => {
  const parts = String(ymd).split('-');
  return Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

/**
 * Inclusive overlap of the payout window and listing contractPeriod.
 * No contract dates → full window. No overlap → 0.
 */
export const availableDaysForContract = (window, contractPeriod) => {
  const daysInWindow = Number(window && window.daysInWindow) || 0;
  if (!window || !window.startDate || !window.endDate) return daysInWindow;
  const period = contractPeriod || {};
  const contractStart = toYmd(period.start);
  const contractEnd = toYmd(period.end);
  if (!contractStart && !contractEnd) return daysInWindow;
  let from = window.startDate;
  let to = window.endDate;
  if (contractStart && contractStart > from) from = contractStart;
  if (contractEnd && contractEnd < to) to = contractEnd;
  if (from > to) return 0;
  return Math.round((utcDay(to) - utcDay(from)) / 86400000) + 1;
};

/** Keep a host-edited value; replace the old unclipped full-window default. */
export const resolveAutoAvailableDays = ({ existing, daysInWindow, computed }) => {
  if (existing == null || existing === '') return computed;
  const n = Number(existing);
  if (!Number.isFinite(n)) return computed;
  if (n === Number(daysInWindow) || n === Number(computed)) return computed;
  return n;
};

export const payoutMethodLabel = value => PAYOUT_CALC_METHODS[resolvePayoutCalcMethod(value)] || PAYOUT_CALC_METHODS.perCheckOut;

export const mergeExpensesSetting = incoming => {
  const next = getDefaultExpensesSetting();
  if (!incoming || typeof incoming !== 'object') return next;
  Object.keys(next).forEach(key => {
    if (incoming[key]) next[key] = incoming[key];
  });
  return next;
};

export const mergeChargesSetting = incoming => {
  const next = getDefaultChargesSetting();
  if (!incoming || typeof incoming !== 'object') return next;
  Object.keys(incoming).forEach(key => {
    next[key] = incoming[key];
    if (CHARGE_KEY_ALIASES[key]) next[CHARGE_KEY_ALIASES[key]] = incoming[key];
  });
  Object.keys(CHARGE_KEY_ALIASES).forEach(v1Key => {
    const v2Key = CHARGE_KEY_ALIASES[v1Key];
    if (next[v1Key] == null && next[v2Key] != null) next[v1Key] = next[v2Key];
  });
  return next;
};

export const chargeValueFromSetting = (setting, key) => {
  const s = setting || {};
  if (s[key]) return s[key];
  if (CHARGE_KEY_ALIASES[key] && s[CHARGE_KEY_ALIASES[key]]) return s[CHARGE_KEY_ALIASES[key]];
  const reverse = Object.keys(CHARGE_KEY_ALIASES).find(v1Key => CHARGE_KEY_ALIASES[v1Key] === key);
  if (reverse && s[reverse]) return s[reverse];
  const defaults = getDefaultChargesSetting();
  if (defaults[key]) return defaults[key];
  if (reverse && defaults[CHARGE_KEY_ALIASES[reverse]]) return defaults[CHARGE_KEY_ALIASES[reverse]];
  return 'host';
};

export const withUpdatedCharges = (chargesSetting, key, val) => {
  const next = Object.assign({}, chargesSetting || {}, { [key]: val });
  if (CHARGE_KEY_ALIASES[key]) next[CHARGE_KEY_ALIASES[key]] = val;
  Object.keys(CHARGE_KEY_ALIASES).forEach(v1Key => {
    if (CHARGE_KEY_ALIASES[v1Key] === key) next[v1Key] = val;
  });
  return next;
};

export const makeEmptyForm = () => ({
  name: '',
  shareType: 'share-by-profit',
  ownerSharePercentage: 70,
  isGroupPackage: false,
  shareProfitBySqft: false,
  groupingType: 'pool',
  packageType: 'profit-sharing',
  units: [],
  unitShareRates: [],
  propertyId: undefined,
  expensesSetting: getDefaultExpensesSetting(),
  chargesSetting: getDefaultChargesSetting(),
  payoutCalcMethod: 'perCheckOut',
  startDay: 1,
  hidePayout: false,
});

export const emptyForm = makeEmptyForm();

export const normalizeId = id => (id && id._id ? String(id._id) : String(id));

export const unitOwnerId = unit => {
  if (!unit || unit.ownedBy == null || unit.ownedBy === '') return undefined;
  if (typeof unit.ownedBy === 'object') {
    const id = unit.ownedBy._id || unit.ownedBy.id;
    return id ? String(id) : undefined;
  }
  const id = String(unit.ownedBy);
  if (!id || id === 'undefined' || id === 'null') return undefined;
  return id;
};

export const unitHasOwner = unit => !!unitOwnerId(unit);

export const assignedUnitsById = (packages, excludePackageId) => {
  const map = {};
  const exclude = excludePackageId ? String(excludePackageId) : '';
  (packages || []).forEach(pkg => {
    const pkgId = normalizeId(pkg && pkg._id);
    if (exclude && pkgId === exclude) return;
    (pkg.units || []).forEach(id => {
      const unitId = normalizeId(id);
      if (!unitId || map[unitId]) return;
      map[unitId] = { packageId: pkgId, packageName: (pkg && pkg.name) || 'another package' };
    });
  });
  return map;
};

export const unitAssignment = (unitId, assignedById) => {
  if (!assignedById) return undefined;
  return assignedById[normalizeId(unitId)];
};

export const isUnitAvailableForPackage = (unit, assignedById) =>
  unitHasOwner(unit) && !unitAssignment(unit && unit._id, assignedById);

export const unitSelectLabel = (unit, assignedById) => {
  const name = (unit && unit.name) || String((unit && unit._id) || '');
  if (!unitHasOwner(unit)) return `${name} (No owner)`;
  const assignment = unitAssignment(unit && unit._id, assignedById);
  if (assignment) return `${name} (In ${assignment.packageName})`;
  return name;
};

export const popupContainer = trigger => trigger.parentNode;

export const labelWithTooltip = (label, title) => (
  <span>
    {label}{' '}
    <Tooltip title={title} overlayStyle={{ maxWidth: 420 }}>
      <Icon type="question-circle" style={{ color: 'rgba(0,0,0,0.45)' }} />
    </Tooltip>
  </span>
);

export const tooltipLines = lines => (
  <span>
    {(lines || []).map((line, index) => (
      <span key={index}>
        {index > 0 ? <br /> : null}
        {line}
      </span>
    ))}
  </span>
);

export const syncUnitShareRates = (selectedUnitIds, existingRates) =>
  (selectedUnitIds || []).map(unitId => {
    const id = String(unitId);
    const match = (existingRates || []).find(item => String(item.unitId) === id);
    return {
      unitId: id,
      ownerSharePercentage: Number(match && match.ownerSharePercentage) || 0,
    };
  });

export const propertyIdFromUnitIds = (unitIds, hostUnits) => {
  const ids = unitIds || [];
  for (let i = 0; i < ids.length; i += 1) {
    const unit = (hostUnits || []).find(u => String(u._id) === String(ids[i]));
    if (!unit) continue;
    const property = unit.roomType && unit.roomType.property;
    const pid = property && property._id ? property._id : property;
    if (pid) return String(pid);
  }
  return undefined;
};

export const formValuesFromPreview = (preview, hostUnits) => {
  const source = preview || {};
  const unitIds = (source.units || []).map(id => String(id && id._id ? id._id : id));
  const isGroupPackage = !!source.isGroupPackage;
  const existingRates = (source.unitShareRates || []).map(item => ({
    unitId: String(item.unitId),
    ownerSharePercentage: Number(item.ownerSharePercentage) || 0,
  }));
  return {
    name: source.name || '',
    shareType: source.shareType || 'share-by-profit',
    ownerSharePercentage: source.ownerSharePercentage == null ? 0 : Number(source.ownerSharePercentage),
    isGroupPackage,
    shareProfitBySqft: !!source.shareProfitBySqft && resolveGroupingType(source.groupingType) !== 'individual',
    groupingType: resolveGroupingType(source.groupingType),
    packageType: source.packageType || 'profit-sharing',
    units: unitIds,
    unitShareRates: isGroupPackage ? syncUnitShareRates(unitIds, existingRates) : [],
    propertyId: propertyIdFromUnitIds(unitIds, hostUnits),
    expensesSetting: mergeExpensesSetting(source.expensesSetting),
    chargesSetting: mergeChargesSetting(source.chargesSetting),
    payoutCalcMethod: resolvePayoutCalcMethod(source.payoutCalcMethod),
    startDay: resolveStartDay(source.startDay),
    hidePayout: !!source.hidePayout,
  };
};

export const buildServicePackagePayload = (values, extras) => {
  const extra = extras || {};
  const groupingType = resolveGroupingType(values.groupingType);
  const payload = {
    name: values.name,
    shareType: values.shareType,
    ownerSharePercentage: values.isGroupPackage ? 0 : values.ownerSharePercentage,
    isGroupPackage: !!values.isGroupPackage,
    shareProfitBySqft: groupingType === 'individual' ? false : !!values.shareProfitBySqft,
    groupingType,
    packageType: values.packageType,
    units: values.units || [],
    unitShareRates: values.isGroupPackage
      ? (values.unitShareRates || []).map(item => ({
          unitId: String(item.unitId),
          ownerSharePercentage: Number(item.ownerSharePercentage) || 0,
        }))
      : [],
    expensesSetting: extra.expensesSetting || mergeExpensesSetting(values.expensesSetting),
    chargesSetting: extra.chargesSetting || mergeChargesSetting(values.chargesSetting),
    payoutCalcMethod: resolvePayoutCalcMethod(
      extra.payoutCalcMethod != null ? extra.payoutCalcMethod : values.payoutCalcMethod
    ),
    startDay: resolveStartDay(extra.startDay != null ? extra.startDay : values.startDay),
    hidePayout: extra.hidePayout != null ? !!extra.hidePayout : !!values.hidePayout,
  };
  return payload;
};

export const validateServicePackageForm = (values, assignedById) => {
  if (!values.name) return 'Name is required';
  const taken = (values.units || [])
    .map(id => unitAssignment(id, assignedById))
    .filter(Boolean);
  if (taken.length) {
    const names = Array.from(new Set(taken.map(item => item.packageName)));
    return `Each unit can only join one service package. Already assigned: ${names.join(', ')}`;
  }
  if (values.isGroupPackage) {
    const unitIds = values.units || [];
    if (!unitIds.length) return 'Select at least one unit for a group package';
    const rateIds = {};
    (values.unitShareRates || []).forEach(item => {
      rateIds[String(item.unitId)] = true;
    });
    const missing = unitIds.some(id => !rateIds[String(id)]);
    if (missing || (values.unitShareRates || []).length !== unitIds.length) {
      return 'Each selected unit must have an owner share percentage';
    }
  }
  const startDay = Number(values.startDay);
  if (!Number.isFinite(startDay) || startDay < 1 || startDay > 28) {
    return 'Payout start day must be between 1 and 28';
  }
  return null;
};

export const findDuplicateUnitIds = (selectedUnitIds, existingPackages) => {
  const used = {};
  (existingPackages || []).forEach(pkg => {
    (pkg.units || []).forEach(id => {
      used[normalizeId(id)] = true;
    });
  });
  const seen = {};
  return (selectedUnitIds || [])
    .map(normalizeId)
    .filter(id => {
      if (!id || !used[id] || seen[id]) return false;
      seen[id] = true;
      return true;
    });
};
